import { useRef, useEffect, useState, useMemo } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { navigationRoutes } from '../../constants/constants'
import { TryIt } from '../try-it-component';
import { ComparisonComponent } from '../comparison-component';
import { FunkyHueCircleThing } from '../ai-assistant-circle-thingy';
import { PricingComponent } from '../pricing-component';
import { TypeAnimation } from '../typing-animation';
import { Background, BackgroundVariant, Node, ReactFlow, MarkerType, Edge, Handle, Position, NodeProps } from "@xyflow/react";
import '@xyflow/react/dist/style.css';
import { getStepIcon } from '../../utils/utils';
import { workflowTemplates } from '../../sample_assets/workflow_templates';
import { getWorkflowExecutionCount } from '../../api/thunks/workflows';
import { HeaderNavComponent } from '../header-nav-compontent';
import { MainBackground } from '../main-background';
import { edgeStyle, nodeTypes } from './react-flow';

export default function SplashPage1() {
    document.title = 'PocketQuant | AI Automation For Finance';
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [workflowExecCount, setWorkflowExecCount] = useState<number | undefined>(undefined);

    // 1. Create refs for each section
    const tryItRef = useRef<HTMLDivElement>(null);
    const comparisonRef = useRef<HTMLDivElement>(null);
    const funkyHueRef = useRef<HTMLDivElement>(null);
    const pricingRef = useRef<HTMLDivElement>(null);

    // 2. Scroll to section on hash change
    useEffect(() => {
        const scrollToHash = () => {
            const hash = window.location.hash.replace('#', '');
            const refs: Record<string, React.RefObject<HTMLDivElement>> = {
                'try-it': tryItRef,
                'comparison': comparisonRef,
                'funky-hue': funkyHueRef,
                'pricing': pricingRef,
            };
            if (hash && refs && refs[hash]?.current) {
                refs[hash]!.current!.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        };
        window.addEventListener('hashchange', scrollToHash);
        // Scroll on mount if hash exists
        if (window.location.hash) {
            setTimeout(scrollToHash, 100); // Ensure elements are rendered
        }
        return () => window.removeEventListener('hashchange', scrollToHash);
    }, []);

    useEffect(() => {
        if (!workflowExecCount) {
            getWorkflowExecutionCount().then((count) => {
                setWorkflowExecCount(count);
            }).catch((err) => {
                console.error("Error fetching workflow execution count:", err);
            })
        }
    }, [])

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    };

    return (
        <MainBackground>
            <HeaderNavComponent/>
            <div className="relative isolate px-6 lg:px-8">
                <div className="mx-auto max-w-7xl min-h-screen flex items-center py-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center w-full">
                        {/* Left column with existing content */}
                        <div className="text-center md:text-left">
                            <div className="hidden sm:mb-8 sm:flex">
                                <div className="relative rounded-full px-3 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-gray-900/20">
                                    Announcing our latest release v2.1
                                    <a href="/changelog" className="font-semibold text-orange-600">
                                        <span className="absolute inset-0" aria-hidden="true" /> Read more <span aria-hidden="true">&rarr;</span>
                                    </a>
                                </div>
                            </div>
                            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
                                Never Miss a Filing. Never Miss a Move.
                            </h1>
                            <p className="mt-6 text-2xl leading-8 text-gray-600">
                                Save time <b><TypeAnimation phrases={shuffleArray([
                                    "by asking the PQ Assistant",
                                    "with workflows that watch the markets",                                    
                                    "reading Earnings Calls",
                                    "connecting the dots",
                                    "reading SEC submissions",
                                    "reading FED announcements",
                                    "reading Executive Orders",
                                    "understanding Unstructured Data",
                                ])} /></b>
                            </p>
                            {/* a counter for the # of workflows executed */}
                            {workflowExecCount && (
                                <>
                                    <p className="mt-6 text-2xl leading-8 text-gray-600">
                                        <span className="text-orange-600 font-bold">{workflowExecCount}</span> workflows run
                                    </p>
                                </>
                            )}
                            <div className="mt-10 flex items-center md:justify-start justify-center gap-x-6">
                                <a
                                    href={"/workflows?templateId=" + workflowTemplates[0].id}
                                    className="rounded-md bg-orange-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
                                >
                                    Start Automating
                                </a>
                                <a
                                    href={"/try-it"}
                                    className="rounded-md bg-orange-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
                                >
                                    Start Chatting
                                </a>
                                <a href="/learning-center" className="text-sm font-semibold leading-6 text-gray-900">
                                    Learn more <span aria-hidden="true">→</span>
                                </a>
                            </div>
                        </div>

                        {/* Right column with ReactFlow */}
                        <div className="h-[500px] border border-gray-200 rounded-lg shadow-md overflow-hidden">
                            <ReactFlowDiagram />
                        </div>
                    </div>
                </div>

                {/* 3. Add id and ref to each section */}
                <div id="try-it" ref={tryItRef}>
                    <TryIt showNav={false} showTabs={false} />
                </div>

                <div id="comparison" ref={comparisonRef}>
                    <ComparisonComponent showNav={false} />
                </div>

                <div id="funky-hue" ref={funkyHueRef}>
                    <FunkyHueCircleThing />
                </div>

                <div id="pricing" ref={pricingRef}>
                    <PricingComponent showNav={false} />
                </div>
            </div>

            {/* Footer Section */}
            <footer className="bg-gray-100 py-6 mt-12">
                <div className="container mx-auto px-6 lg:px-8">
                    <div className="flex justify-center space-x-6">
                        <a href="/terms-of-service" className="text-sm text-gray-600 hover:text-gray-900">
                            Terms of Service
                        </a>
                        <a href="/privacy-policy" className="text-sm text-gray-600 hover:text-gray-900">
                            Privacy Policy
                        </a>
                        <a href="/anti-scraping" className="text-sm text-gray-600 hover:text-gray-900">
                            Anti-Scraping Policy
                        </a>
                    </div>
                </div>
            </footer>
        </MainBackground>
    )
}

function ReactFlowDiagram() {
    const [selectedWorkflow, setSelectedWorkflow] = useState('earnings-call');

    // Create a node using the custom component
    const selectorNode = useMemo(() => ({
        id: 'workflow-selector',
        type: 'selector',
        position: { x: 100, y: -150 },
        data: {
            selectedWorkflow,
            setSelectedWorkflow
        }
    }), [selectedWorkflow, setSelectedWorkflow]);

    // Dynamically create nodes based on selected workflow
    const workflowNodes = useMemo(() => {
        switch (selectedWorkflow) {
            case 'earnings-call':
                return [
                    {
                        id: '1',
                        type: 'input',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("data-source")}
                                    <span className="ml-2">Earnings Call</span>
                                </div>
                            )
                        },
                        position: { x: 50, y: 50 },
                        style: { background: '#f97316', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '2',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("llm")}
                                    <span className="ml-2">AI Answers Question</span>
                                </div>
                            )
                        },
                        position: { x: 250, y: 150 },
                        style: { background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '3',
                        type: 'output',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("email")}
                                    <span className="ml-2">Email Me</span>
                                </div>
                            )
                        },
                        position: { x: 450, y: 50 },
                        style: { background: '#10b981', color: 'white', border: 'none', borderRadius: '8px' }
                    }
                ];
            case 'key-figures':
                return [
                    {
                        id: '1',
                        type: 'input',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("data-source")}
                                    <span className="ml-2">TSLA 8-K</span>
                                </div>
                            )
                        },
                        position: { x: 50, y: 0 },
                        style: { background: '#f97316', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '2',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("decision")}
                                    <span className="ml-2">Is this a deliveries report?</span>
                                    <Handle type="source" position={Position.Right} id="decision-no" />
                                    <Handle type="source" position={Position.Bottom} id="decision-yes" />
                                </div>
                            )
                        },
                        position: { x: 250, y: 0 },
                        style: { background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '5',
                        data: {
                            label: (
                                <div >
                                    End
                                    <Handle type="target" position={Position.Left} id="terminal-left" />
                                </div>
                            )
                        },
                        position: { x: 450, y: 0 },
                        style: { background: '#FF0000', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '3',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("data-extraction")}
                                    <span className="ml-2">Extract deliveries</span>
                                </div>
                            )
                        },
                        position: { x: 50, y: 100 },
                        style: { background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '4',
                        type: 'output',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("api-destination")}
                                    <span className="ml-2">API Destination</span>
                                </div>
                            )
                        },
                        position: { x: 250, y: 100 },
                        style: { background: '#10b981', color: 'white', border: 'none', borderRadius: '8px' }
                    }
                ];

            case '8k-analysis':
                return [
                    {
                        id: '1',
                        type: 'input',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("data-source")}
                                    <span className="ml-2">8-K Filing</span>
                                </div>
                            )
                        },
                        position: { x: 150, y: 0 },
                        style: { background: '#f97316', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '2',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("llm")}
                                    <span className="ml-2">Risk Analysis</span>
                                </div>
                            )
                        },
                        position: { x: 350, y: 0 },
                        style: { background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '3',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("decision")}
                                    <span className="ml-2">Material Risk?</span>
                                </div>
                            )
                        },
                        position: { x: 150, y: 100 },
                        style: { background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px' }
                    },
                    {
                        id: '4',
                        type: 'output',
                        data: {
                            label: (
                                <div className="flex items-center">
                                    {getStepIcon("email")}
                                    <span className="ml-2">Urgent Alert</span>
                                </div>
                            )
                        },
                        position: { x: 350, y: 100 },
                        style: { background: '#10b981', color: 'white', border: 'none', borderRadius: '8px' }
                    }
                ];

            default:
                return [];
        }
    }, [selectedWorkflow]);

    // Get workflow edges based on the selected workflow
    const workflowEdges = useMemo(() => {
        switch (selectedWorkflow) {
            case 'earnings-call':
                return [
                    { id: 'e1-2', source: '1', target: '2', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                    { id: 'e2-3', source: '2', target: '3', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                ];
            case 'key-figures':
                return [
                    { id: 'e1-2', source: '1', target: '2', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                    { id: 'e2-3', source: '2', target: '3', label: "Yes", sourceHandle: "decision-yes", animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                    { id: 'e3-4', source: '3', target: '4', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                    { id: 'e2-5', source: '2', target: '5', label: "No", sourceHandle: "decision-no", targetHandle: "terminal-left", animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                ];
            case '8k-analysis':
                return [
                    { id: 'e1-2', source: '1', target: '2', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                    { id: 'e2-3', source: '2', target: '3', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                    { id: 'e3-4', source: '3', target: '4', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
                ];
            default:
                return [];
        }
    }, [selectedWorkflow]);

    // Combine workflow nodes with selector node
    const nodes = useMemo(() => [selectorNode, ...workflowNodes], [selectorNode, workflowNodes]);
    
    const edges = useMemo(() => workflowEdges.map(edge => ({
        ...edge,
        style: edgeStyle,
    })), [workflowEdges]);


    return (
        <ReactFlow
            nodes={nodes}
            edges={edges}
            fitView
            nodeTypes={nodeTypes}
        >
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        </ReactFlow>
    );
}